import { Edit2, Trash2, Calendar, Clock, Tag, ChevronRight, ExternalLink, Hash, Sun, X, Repeat, Mail, ArrowRight, Hourglass, Eye, RefreshCw, CheckCircle2, XCircle, UserCircle, CalendarClock } from 'lucide-react';
import Checkbox from './Checkbox';
import { StatusActions, ConvertDropdown, OverflowMenu } from './quick-actions';
import { TASK_STATUSES, TASK_CONTEXTS, computeStatus, DELEGATION_STATUSES, isDelegated, getDelegateDisplayName } from '../hooks/useTasks';
import { getTodayString } from '../utils/dateUtils';

// Delegation status configuration
const delegationStatusConfig = {
  [DELEGATION_STATUSES.PENDING]: { label: 'Pending', icon: Hourglass, color: 'text-amber-400', bg: 'bg-amber-500/15' },
  [DELEGATION_STATUSES.ACKNOWLEDGED]: { label: 'Seen', icon: Eye, color: 'text-helmAccent', bg: 'bg-helmAccent/15' },
  [DELEGATION_STATUSES.IN_PROGRESS]: { label: 'Working', icon: RefreshCw, color: 'text-weekly', bg: 'bg-weekly/15' },
  [DELEGATION_STATUSES.COMPLETED]: { label: 'Done', icon: CheckCircle2, color: 'text-success', bg: 'bg-success/15' },
  [DELEGATION_STATUSES.DECLINED]: { label: 'Declined', icon: XCircle, color: 'text-error', bg: 'bg-error/15' }
};

function formatDateShort(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Source icons and colors
const SOURCE_CONFIG = {
  slack: { icon: Hash, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  email: { icon: Tag, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  manual: { icon: null, color: 'text-text-tertiary', bg: '' }
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return dateStr < getTodayString();
}

const priorityColors = {
  low: 'text-text-tertiary',
  normal: 'text-text-secondary',
  high: 'text-red-400'
};

const statusBadges = {
  [TASK_STATUSES.INBOX]: { label: 'Inbox', bg: 'bg-surface-hover', text: 'text-text-tertiary' },
  [TASK_STATUSES.TODAY]: { label: 'Today', bg: 'bg-accent-muted', text: 'text-accent' },
  [TASK_STATUSES.SCHEDULED]: { label: 'Scheduled', bg: 'bg-weekly-light', text: 'text-weekly' },
  [TASK_STATUSES.WAITING]: { label: 'Waiting', bg: 'bg-amber-500/15', text: 'text-amber-400' },
  [TASK_STATUSES.SOMEDAY]: { label: 'Someday', bg: 'bg-purple-500/15', text: 'text-purple-400' },
  [TASK_STATUSES.DONE]: { label: 'Done', bg: 'bg-success-light', text: 'text-success' }
};

export default function TaskItem({
  task,
  onToggleComplete,
  onEdit,
  onDelete,
  onQuickAction,
  onAddToToday,
  onRemoveFromToday,
  onSetFollowUp,
  // Quick action props for status changes
  onSchedule,
  onMoveToWaiting,
  onMoveToSomeday,
  // Conversion quick action props (for inbox tasks)
  onCreateProject,
  onAddToOneOnOne,
  onAddAsGoal,
  onAddAsHabit,
  showQuickActions = false,
  showConversionActions = false,
  useOverflowMenu = false,
  showTodayInMenu = false,     // Show "Today" in overflow menu
  hideWaitingInMenu = false,   // Hide "Waiting" in overflow menu
  hideScheduleInMenu = false,  // Hide "Schedule" in overflow menu
  showStatus = false,
  showContext = true,
  compact = false,
  isAdmin = true,
  isFollowUp = false,
  isOverdueSection = false,
  delegates = []
}) {
  const taskStatus = computeStatus(task);
  const isDone = taskStatus === TASK_STATUSES.DONE;
  const context = TASK_CONTEXTS.find(c => c.id === task.context);
  const statusBadge = statusBadges[taskStatus];
  const dueDateOverdue = isOverdue(task.dueDate) && !isDone;

  if (compact) {
    return (
      <div
        className={`
          group flex items-center gap-2 p-2
          bg-surface-card/60 hover:bg-surface-card
          rounded-lg border border-border hover:border-border-strong
          transition-all duration-200
          ${isDone ? 'opacity-50' : ''}
        `}
      >
        <Checkbox
          checked={isDone}
          onChange={onToggleComplete}
        />

        <div className="flex-1 min-w-0">
          <p className={`text-xs text-text-primary truncate ${isDone ? 'line-through' : ''}`}>
            {task.title}
          </p>
        </div>

        {task.dueDate && (
          <span className={`text-[10px] ${dueDateOverdue ? 'text-red-400 font-medium' : 'text-text-tertiary'}`}>
            {formatDate(task.dueDate)}
          </span>
        )}

        {isAdmin && (
          <button
            onClick={onEdit}
            className="p-1 rounded-md bg-transparent border-none text-text-muted cursor-pointer md:opacity-0 md:group-hover:opacity-100 hover:bg-surface-hover hover:text-text-secondary transition-all"
          >
            <Edit2 size={12} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={() => onEdit && onEdit()}
      className={`
        group flex items-start gap-3 p-3
        bg-surface-card/60 hover:bg-surface-card
        rounded-lg border transition-all duration-200 cursor-pointer
        ${task.priority === 'high' && !isDone ? 'border-red-500/30' : 'border-border hover:border-border-strong'}
        ${isDone ? 'opacity-50' : ''}
      `}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isDone}
          onChange={onToggleComplete}
        />
      </div>

      <div className="flex-1 min-w-0">
        {/* Follow-up badge - shows prominently when this is a follow-up on a delegated task */}
        {isFollowUp && isDelegated(task) && !task._isDelegatedToMe && (
          <div className="flex items-center gap-2 mb-1.5">
            <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-semibold flex items-center gap-1">
              <CalendarClock size={10} />
              FOLLOW UP
            </span>
            <span className="text-xs text-text-primary font-medium">
              with {getDelegateDisplayName(task)}
            </span>
            {/* Show delegation status next to name */}
            {(() => {
              const status = task.delegationStatus || DELEGATION_STATUSES.PENDING;
              const statusCfg = delegationStatusConfig[status] || delegationStatusConfig[DELEGATION_STATUSES.PENDING];
              const StatusIcon = statusCfg.icon;
              return (
                <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${statusCfg.bg} ${statusCfg.color}`}>
                  <StatusIcon size={10} />
                  {statusCfg.label}
                </span>
              );
            })()}
          </div>
        )}

        {/* Title */}
        <p className={`text-sm text-text-primary ${isDone ? 'line-through' : ''} ${task.priority === 'high' ? 'font-medium' : ''}`}>
          {task.title}
        </p>

        {/* Notes preview */}
        {task.notes && (
          <p className="text-xs text-text-tertiary mt-0.5 line-clamp-1">
            {task.notes}
          </p>
        )}

        {/* Meta row */}
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {/* Delegated by badge - shows when task was delegated TO me */}
          {task._isDelegatedToMe && task.delegatedByName && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-helmAccent/15 text-helmAccent flex items-center gap-1">
              <UserCircle size={10} />
              From: {task.delegatedByName}
            </span>
          )}

          {/* Status badge - hide when delegated (delegate name implies waiting) */}
          {showStatus && statusBadge && !isDelegated(task) && !task.waitingFor && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusBadge.bg} ${statusBadge.text}`}>
              {statusBadge.label}
            </span>
          )}

          {/* Context */}
          {showContext && context && (
            <span className={`text-[10px] ${context.color}`}>
              {context.label}
            </span>
          )}

          {/* Due date */}
          {task.dueDate && (
            <span className={`text-[10px] flex items-center gap-0.5 ${dueDateOverdue ? 'text-red-400 font-medium' : 'text-text-tertiary'}`}>
              <Calendar size={10} />
              {dueDateOverdue && 'OVERDUE - '}
              {formatDate(task.dueDate)}
            </span>
          )}

          {/* Scheduled date */}
          {task.scheduledDate && taskStatus === TASK_STATUSES.SCHEDULED && (
            <span className="text-[10px] flex items-center gap-0.5 text-weekly">
              <Clock size={10} />
              {formatDate(task.scheduledDate)}
            </span>
          )}

          {/* Waiting for (non-delegation waiting) */}
          {task.waitingFor && !isDelegated(task) && taskStatus === TASK_STATUSES.WAITING && (
            <span className="text-[10px] text-amber-400">
              → {task.waitingFor}
            </span>
          )}

          {/* Delegated to - simplified: just show name (implies waiting) */}
          {isDelegated(task) && (() => {
            const delegateName = getDelegateDisplayName(task);
            const status = task.delegationStatus || DELEGATION_STATUSES.PENDING;
            const statusCfg = delegationStatusConfig[status] || delegationStatusConfig[DELEGATION_STATUSES.PENDING];
            const StatusIcon = statusCfg.icon;
            // Only show status badge if NOT pending (pending is the default/implied state)
            const showDelegationStatus = status !== DELEGATION_STATUSES.PENDING;

            return (
              <div className="flex items-center gap-2 flex-wrap">
                {/* Delegate name - this alone implies "waiting on this person" */}
                <span className="text-[10px] text-amber-400 flex items-center gap-0.5">
                  <ArrowRight size={10} />
                  {delegateName}
                </span>

                {/* Delegation status badge - only show if changed from pending */}
                {showDelegationStatus && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${statusCfg.bg} ${statusCfg.color}`}>
                    <StatusIcon size={10} />
                    {statusCfg.label}
                  </span>
                )}
              </div>
            );
          })()}

          {/* Focused for today indicator - hide in Today view (useOverflowMenu) since it's redundant */}
          {task.focusedForToday && !isOverdueSection && !useOverflowMenu && (
            <span className="text-[10px] text-accent flex items-center gap-0.5">
              <Sun size={10} />
              Today
            </span>
          )}

          {/* Recurring indicator */}
          {task.isRecurring && (
            <span className="text-[10px] text-purple-400 flex items-center gap-0.5">
              <Repeat size={10} />
              Weekly
            </span>
          )}

          {/* Source (Slack channel with link) */}
          {task.source === 'slack' && task.sourceChannel && (
            <a
              href={task.sourceLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] flex items-center gap-0.5 text-purple-400 hover:text-purple-300 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Hash size={10} />
              {task.sourceChannel}
              <ExternalLink size={8} className="ml-0.5 opacity-60" />
            </a>
          )}

          {/* Source (Email with link) */}
          {task.source === 'email' && task.sourceLink && (
            <a
              href={task.sourceLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] flex items-center gap-0.5 text-blue-400 hover:text-blue-300 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Mail size={10} />
              Open Email
              <ExternalLink size={8} className="ml-0.5 opacity-60" />
            </a>
          )}
        </div>

        {/* Quick Actions - Full inline buttons for Inbox */}
        {showQuickActions && !useOverflowMenu && (
          <div className="mt-2 flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
            {/* Left side: Status actions */}
            <div className="flex items-center gap-1 flex-wrap">
              <StatusActions
                task={task}
                onAddToToday={() => onAddToToday?.()}
                onSchedule={onSchedule}
                onMoveToWaiting={onMoveToWaiting}
                onMoveToSomeday={onMoveToSomeday}
                onAddToOneOnOne={showConversionActions ? () => onAddToOneOnOne?.(task) : undefined}
                onComplete={onToggleComplete}
                showDelete={false}
                showDone={false}
                showOneOnOne={showConversionActions && taskStatus === TASK_STATUSES.INBOX}
                monochrome={showConversionActions}
                delegates={delegates}
              />
            </div>

            {/* Right side: Convert dropdown + Delete */}
            <div className="flex items-center gap-1.5">
              {/* Convert dropdown for inbox tasks */}
              {showConversionActions && taskStatus === TASK_STATUSES.INBOX && (
                <ConvertDropdown
                  onCreateProject={() => onCreateProject?.(task)}
                  onAddAsGoal={() => onAddAsGoal?.(task)}
                  onAddAsHabit={() => onAddAsHabit?.(task)}
                />
              )}
              {/* Delete button */}
              {isAdmin && onDelete && (
                <button
                  onClick={() => onDelete?.(task.id)}
                  className="p-1 rounded-md border-none cursor-pointer transition-all duration-200 text-text-muted bg-transparent hover:text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Overflow menu - "..." dropdown for processed views (replaces inline buttons) */}
      {useOverflowMenu && (
        <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <OverflowMenu
            task={task}
            onSchedule={onSchedule}
            onMoveToWaiting={onMoveToWaiting}
            onMoveToSomeday={onMoveToSomeday}
            onAddToToday={onAddToToday}
            onDelete={onDelete}
            showDelete={isAdmin && !!onDelete}
            showToday={showTodayInMenu}
            hideWaiting={hideWaitingInMenu}
            hideSchedule={hideScheduleInMenu}
            delegates={delegates}
          />
        </div>
      )}

      {/* Edit button - visible on mobile, hover on desktop (only when quick actions and overflow not shown) */}
      {!showQuickActions && !useOverflowMenu && (
        <div className="flex items-center gap-0.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          {isAdmin && onEdit && (
            <button
              onClick={onEdit}
              className="p-1.5 rounded-md bg-transparent border-none text-text-muted cursor-pointer hover:bg-surface-hover hover:text-text-secondary transition-all"
            >
              <Edit2 size={12} />
            </button>
          )}
        </div>
      )}

      {/* Follow-up quick actions (shown for follow-up items) */}
      {isFollowUp && !isDone && (
        <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onToggleComplete}
            className="px-2 py-1 text-[10px] rounded bg-success/10 text-success border-none cursor-pointer hover:bg-success/20 transition-all"
          >
            ✓ Done
          </button>
          {onSetFollowUp && (
            <button
              onClick={onSetFollowUp}
              className="px-2 py-1 text-[10px] rounded bg-amber-500/10 text-amber-400 border-none cursor-pointer hover:bg-amber-500/20 transition-all"
            >
              📅 New Date
            </button>
          )}
        </div>
      )}
    </div>
  );
}
